import React, { useEffect, useMemo, useState } from "react";
import "react-confirm-alert/src/react-confirm-alert.css"; // Import css
import "./css/mdb.min.css";
import "./font/fontawesome/css/all.min.css";
import "./css/custom/animations.css";
import "./css/custom/tweeks.css";
// import './css/bootstrap5.min.css';
import "./css/styles/main.css";
import "./css/styles/responsive.css";

import { Routes, Route, useNavigate, useLocation } from "react-router-dom";

import ValuesStore from "./js/store/values-store";
import Utilities from "./js/dependencies/custom/react-utilities";
import Settings from "./js/dependencies/custom/settings";
import ProtectedRoute from "./js/components/comps/protected_route";

import financeRoutes from "./js/routes/finance";
import botRoutes from "./js/routes/bot";
import PublicRoutes from "./js/routes/Public";
import ApplicantRoutes from "./js/routes/Applicant";
import useAcademicRoutes_mk from "./js/routes/Academics_mk";
import StudentRoutes from "./js/routes/Student";
import useAdminRoutes from "./js/routes/Admin";
import table_meta from "./js/store/table_meta";
import WelcomePage from "./js/pages/Welcome";
import Main from "./js/components/layout/Main";
import academicsRoutes from "./js/routes/academics";
import parentRoutes from "./js/routes/parent";
import auditRoutes from "./js/routes/audit";
import lmsRoutes from "./js/routes/lms";
import SessionExpiredModal from "./js/components/comps/SessionExpiredModal";
import SessionTimeoutWarning from "./js/components/comps/SessionTimeoutWarning";

function App() {
  const [jwtValid, setJWTValid] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const adminRoute = useAdminRoutes();
  const academicRoutes_mk = useAcademicRoutes_mk();
  const privateRoutes = [
    ...ApplicantRoutes,
    ...adminRoute,
    ...botRoutes,
    ...financeRoutes,
    ...academicRoutes_mk,
    ...StudentRoutes,
    ...academicsRoutes,
    ...parentRoutes,
    ...auditRoutes,
    ...lmsRoutes,
  ];

  const [hasBootstrapped, setHasBootstrapped] = useState(false);
  const valuesStore = ValuesStore();
  const navigate = useNavigate();

  const newLocation = useLocation();

  async function bootstrap() {
    if (!hasBootstrapped) {
      Utilities.startTokenRefreshTimer();
      const res = await Utilities.fetchWithAuth(
        "GET",
        Settings.urls.bootstrap.setup,
        undefined,
        navigate,
      );
      // console.log(res.data);
      if (res?.ok) {
        const d = res?.data;
        for (let key in d) {
          valuesStore.setValue(key, d?.[key]);
        }
        setHasBootstrapped(true);
      }
      // console.log(res);
    }
  }

  const fetchPermissions = async () => {
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.rbac?.myperms,
      undefined,
      navigate,
    );

    if (res?.ok) {
      const perms = res?.data?.permissions || [];
      const roles = res?.data?.roles || [];
      const a = Utilities.removeDuplicateUsingKey(perms, "name");
      valuesStore.setValue("permissions", a);
      valuesStore.setValue("roles", roles);
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
  };

  const fetchMyRoles = async () => {
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings?.urls?.rbac?.myRoles,
      undefined,
      navigate,
    );
    if (res?.ok) {
      valuesStore.setValue("myRoles", res?.data || []);
    }
  };

  async function verifyJWT() {
    try {
      if (!Utilities.getCookie("access") && Utilities.hasActiveSession()) {
        Utilities.restoreSessionToTab();
      }

      const token = Utilities.getCookie("access");
      if (!token) {
        setJWTValid(false);
        Utilities.logout(navigate, "access", "refresh", "../");
        return;
      }

      const res = await Utilities.fetchWithAuth(
        "POST",
        Settings.urls.auth.verify_jwt,
        { token },
        navigate,
      );

      if (res?.ok) {
        if (!res?.data?.is_valid) {
          setJWTValid(false);
          Utilities.logout(navigate, "access", "refresh", "../");
        } else {
          setJWTValid(true);
        }
      } else {
        // If server says not ok, force logout
        setJWTValid(false);
        Utilities.logout(navigate, "access", "refresh", "../");
      }
    } catch (error) {
      setJWTValid(false);
      Utilities.logout(navigate, "access", "refresh", "../");
    }
  }

  const authPages = ["/signin", "/signup", "/", "/reset", "/recover_password"];

  useEffect(() => {
    const isPublicAuthPage = authPages.includes(newLocation.pathname);

    if (isPublicAuthPage) {
      setJWTValid(false);

      if (Utilities.hasActiveSession()) {
        Utilities.restoreSessionToTab();
        navigate("/welcome/me", { replace: true });
      }

      return;
    }

    if (
      ![...authPages, "/redeem_coupon", "/otp"].includes(newLocation.pathname)
    ) {
      verifyJWT();
      setTimeout(() => {
        valuesStore.setValue("tables_metadata", table_meta(valuesStore));
        bootstrap();
        fetchPermissions();
        fetchMyRoles();
      }, 500);
    }
  }, [newLocation.pathname]);

  // Register session expiry callback
  useEffect(() => {
    Utilities.onSessionExpired(() => {
      setSessionExpired(true);
    });
    return () => {
      Utilities.onSessionExpired(null);
      Utilities.stopTokenRefreshTimer();
    };
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== "edunex_session_lock" || event.newValue !== null) {
        return;
      }

      valuesStore.resetAuthState?.();
      setJWTValid(false);
      navigate("/signin", { replace: true });
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [navigate, valuesStore]);

  return (
    <>
      <Routes>
        {/* Public routes */}
        {PublicRoutes?.map((route, i) => (
          <Route key={i} path={route.path} element={route.element}>
            {route.children?.map((child, j) => (
              <Route key={j} path={child.path} element={child.element} />
            ))}
          </Route>
        ))}

        {/* Protected routes */}
        {jwtValid && (
          <>
            {privateRoutes
              .filter((route) => route.path !== "/admin")
              .map((route, i) => (
                <Route key={i} path={route.path} element={route.element}>
                  {route.children?.map((child, j) => (
                    <Route
                      key={`${i}-${j}`}
                      path={child.path}
                      element={child.element}
                    />
                  ))}
                </Route>
              ))}

            {/* Single /admin layout — all admin pages share one Main so the
                sidebar stays mounted when navigating between route groups */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Main />
                </ProtectedRoute>
              }
            >
              {privateRoutes
                .filter((route) => route.path === "/admin")
                .flatMap((route) => route.children ?? [])
                .map((child, j) => (
                  <Route
                    key={`admin-${j}-${child.path}`}
                    path={child.path}
                    element={
                      <ProtectedRoute>{child.element}</ProtectedRoute>
                    }
                  />
                ))}
            </Route>
          </>
        )}

        {/* Layout */}
        <Route path="/welcome" element={<Main />}>
          <Route path="me" element={<WelcomePage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<></>} />
      </Routes>

      <SessionExpiredModal
        open={sessionExpired}
        onClose={() => setSessionExpired(false)}
      />

      {jwtValid && <SessionTimeoutWarning />}
    </>
  );
}

//disable auto zoom in on iphone and ipad. iphone and ipad automatically zoom in when text size is less than 16px
if (Utilities.checkIsIOS()) {
  Utilities.addMaximumScaleToMetaViewport();
}

export default App;
